import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { MARKET_OPTIONS } from 'src/utils/markets';

export interface TimelineTitleFields {
  evento?: string | null;
  assunto?: string | null;
  descricaofundo?: string | null;
  tipodocumento?: string | null;
}

export interface TimelineOperationPathFields {
  market?: string | null;
  slug?: string | null;
  hashId?: string | null;
}

const supportedOperationMarkets = new Set(
  MARKET_OPTIONS.map((option) => option.toLocaleLowerCase('pt-BR')),
);

export const getTimelineOperationPath = ({
  market,
  slug,
  hashId,
}: TimelineOperationPathFields) => {
  const marketSegment = market?.trim().toLocaleLowerCase('pt-BR');
  const slugSegment = slug?.trim();
  const hashIdSegment = hashId?.trim();

  if (
    !marketSegment ||
    !supportedOperationMarkets.has(marketSegment) ||
    !slugSegment ||
    !hashIdSegment
  ) {
    return undefined;
  }

  return `/operacao/${marketSegment}/${slugSegment}/${hashIdSegment}`;
};

export const getTimelineRecordTitle = ({
  evento,
  assunto,
  descricaofundo,
  tipodocumento,
}: TimelineTitleFields) =>
  evento || assunto || descricaofundo || tipodocumento || '';

export const normalizePoints = (
  value: unknown,
  ancestors: WeakSet<object> = new WeakSet(),
): string | undefined => {
  if (typeof value === 'string') return value.trim() || undefined;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (value === null || typeof value !== 'object') return undefined;
  if (ancestors.has(value)) return undefined;

  ancestors.add(value);

  try {
    const values = Array.isArray(value)
      ? value
      : Object.values(value as Record<string, unknown>);

    if (values.length === 0) return undefined;

    const extractedValues = values
      .map((item) => normalizePoints(item, ancestors))
      .filter((item): item is string => Boolean(item));

    if (extractedValues.length > 0) return extractedValues.join('; ');
    return undefined;
  } catch {
    return undefined;
  } finally {
    ancestors.delete(value);
  }
};

export const getTimelineRecordSummary = (
  summary: string | null | undefined,
  points: unknown,
) => summary?.trim() || normalizePoints(points);

export const formatTimelineDate = (date?: string | null) => {
  if (!date) return '';

  const parsedDate = parseISO(date);

  if (Number.isNaN(parsedDate.getTime())) return date;

  return format(parsedDate, "dd 'de' MMM 'de' yyyy", { locale: ptBR }).replace(
    /\./g,
    '',
  );
};
